import * as React from 'react';
import { get } from 'lodash';
import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { Text } from '@lightspeed/flame/Text';

import { createReactRenderer } from 'helpers/react-renderer';
import Format from 'app/src/data/Format';
import Localize from 'app/src/localize/Localize';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import PaymentOperations from 'app/src/payments/PaymentOperations';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import TransactionResultModal from 'payments/components/TransactionResultModal';
import Payment from 'app/src/data/models/Payment';
import Monitoring from 'shared_modules/Monitoring';

import CommonData from 'app/src/data/CommonData';
import TerminalPaymentModal from 'payments/TerminalPayment';
import { usePaymentContext } from '../Store/PaymentState';

const supportMessage = Localize.translate(
  `Unable to void payment. Try again and if the problem persists, contact <a class="strong-link" target="_blank" href="https://www.lightspeedhq.com/support/retail">Support</a>`
);

const approvedPaymentRow = ({ payment, onRemove }: TSFixMe) => {
  const paymentJSON = payment.toJSON();
  const details = payment.details;

  const cardInformation = (() => {
    let cardInformation = '';
    let type = Localize.translate('Credit Card');
    if (details) {
      if (details.isDebit) {
        type = Localize.translate('Debit');
      }
      if (details.cardType) {
        cardInformation += `${details.cardType}`;
      }
      if (details.maskedDigits) {
        cardInformation += ` (${details.maskedDigits || '●●●●'})`;
      }
    }

    return { type, cardInformation };
  })();

  return (
    <Flex
      key={paymentJSON.paymentID}
      padding={1}
      role="listitem"
      aria-label={`approved payment ${cardInformation.type} ${cardInformation.cardInformation}`}
    >
      <Text
        pr={1}
        style={{ wordBreak: 'break-word', wordWrap: 'break-word' }}
        textTransform="capitalize"
      >
        {cardInformation.type} {cardInformation.cardInformation}
      </Text>
      <Flex className="payment-amount" alignSelf="center">
        {Format.formatMoney(paymentJSON.amount, 0)}
      </Flex>
      <Flex margin={1} alignSelf="center">
        <Button className="remove-emv-payment" onClick={() => onRemove({ payment })}>
          Remove
        </Button>
      </Flex>
    </Flex>
  );
};

export const ApprovedPaymentsList: React.FC = () => {
  const { paymentProvider, saleManager } = usePaymentContext();

  const [approvedPaymentList, setApprovedPaymentList] = React.useState([]);

  const refundInteracPaymentTransaction = React.useCallback(
    paymentModel => {
      const refundableAmount = paymentModel.model.get('amount').negated();
      const refundedPaymentID = paymentModel.model.get('paymentID');
      const terminal = paymentProvider.getTerminal(CommonData.selectedRegister.get('ccTerminalID'));
      if (refundableAmount && refundedPaymentID && terminal) {
        TerminalPaymentModal.doTerminalPayment({
          initialAmount: refundableAmount,
          terminal,
          saleManager,
          paymentProvider,
          isManualEntry: false,
          isUnreferencedRefund: false,
          refundedPaymentID,
          recoveredPayment: null,
        })
          .then(() => {
            // doTerminalPayment will fetch the new sale and refresh saleManager after it has been removed.
            setApprovedPaymentList(saleManager.creditDebitPayments);
          })
          .catch(() => {
            PaymentServiceAlert.alert(supportMessage);
          });
      } else {
        if (!refundableAmount || !refundedPaymentID) {
          PaymentServiceAlert.alert(supportMessage);
        }

        if (!terminal) {
          PaymentServiceAlert.alert(
            Localize.translate(
              `Unable to find the terminal used for this transaction. Finish this sale, then refund it.`
            )
          );
        }
      }
    },
    [paymentProvider, saleManager]
  );

  /**
   * Event handler for when a remove button is clicked from within the list.
   *
   */
  const onPaymentRemove = React.useCallback(
    ({ payment }) => {
      const paymentModel = PaymentFactory.createPaymentFromModel(payment);

      // should be new terminals that utilize the payment service so make sure we have a payment ID
      if (paymentModel.isServicePayment()) {
        const cardType = get(paymentModel, 'model.details.cardType');
        if (cardType === PaymentTransaction.SupportedCardType.INTERAC) {
          refundInteracPaymentTransaction(paymentModel);
        } else {
          PaymentOperations.doVoidOperation(paymentModel, saleManager, paymentProvider)
            .done(response => {
              // doVoidOperation will fetch the new sale and refresh saleManager after it has been removed.
              setApprovedPaymentList(saleManager.creditDebitPayments);
              if (
                paymentProvider.activeGateway.get('gatewayID') ===
                  GatewayConfig.GatewayType.VANTIV_TRIPOS ||
                paymentProvider.activeGateway.get('gatewayID') ===
                  GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS
              ) {
                const parameters = response?.serviceModel?.paymentTransaction?.parameters || {};
                const message = parameters.message || response.error;
                const detailedMessage = parameters.detailedMessage || null;
                const RenderedComponent = createReactRenderer({
                  component: TransactionResultModal,
                  selector: '[data-react-root="ReactModal"]',
                  props: {
                    onHide: () => {
                      RenderedComponent.unmount();
                    },
                    amount: response.model.get('amount'),
                    message,
                    detailedMessage,
                    paymentDetails: {
                      cardBrand: get(response, 'model.details.cardBrand', 'Unknown'),
                      maskedDigits: get(response, 'model.details.maskedDigits', ''),
                    },
                    salePaymentID: response.serviceModel
                      .get('SalePayment')
                      .salePaymentID.toString(),
                    status: Payment.Status.VOIDED,
                    systemCustomerID: window.merchantos.session.getSystemCustomerID(),
                  },
                });

                RenderedComponent.mount();
              } else {
                PaymentServiceAlert.alert(supportMessage);
              }
            })
            .fail(() => {
              PaymentServiceAlert.alert(supportMessage);
            });
        }
      } else {
        // We reach this case when the payment type is no-longer accessible.
        // If the Payment Service goes down this could be "user defined" or
        // any other type that won't match any of the following types:
        // [TerminalPayment, GiftCardPayment, SwipePayment, DepositPayment]
        Monitoring.NRError(
          new Error(`Merchant received an error while removing a payment in a sale`),
          'AcceptedPaymentList',
          {
            reason: `The payment type "${payment.constructor.name}" is not an instance of [TerminalPayment, GiftCardPayment, SwipePayment, DepositPayment]`,
          }
        );

        PaymentServiceAlert.alert(supportMessage);
      }
    },
    [paymentProvider, refundInteracPaymentTransaction, saleManager]
  );

  React.useEffect(() => {
    if (saleManager && saleManager.creditDebitPayments) {
      setApprovedPaymentList(saleManager.creditDebitPayments);
    }
  }, [saleManager]);

  return (
    <div
      id="emv_payments_list"
      role="list"
      className="emv-payments"
      aria-label="special payments list"
    >
      {approvedPaymentList.map(payment =>
        approvedPaymentRow({ payment, onRemove: onPaymentRemove })
      )}
    </div>
  );
};
